import React, { useState, useEffect } from "react";
import { btnP, crdEl, sp, rad, bdg, icBox } from "../../constants/styles";
import { api } from "../../api";
import { formatPrice } from "../../utils/plans";

/**
 * Dynamic Upgrade Modal — fetches plans from API and lets user pick one.
 * Props: { h, t, used, limit, currentPlan, onClose, onUpgradeSuccess }
 */
export default function UpgradeModal({ h, t, used, limit, currentPlan, onClose, onUpgradeSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(true);

  // Fetch plans from API
  useEffect(() => {
    api.getPlans().then(d => {
      if (d.success && d.plans) {
        // Filter to paid plans only, sorted by sort_order
        const paid = (d.plans || []).filter(p => p.price > 0 && p.is_active);
        setPlans(paid);
        // Auto-select recommended plan (first plan above current, or first paid)
        const currentSort = d.plans.find(p => p.id === (currentPlan || "free"))?.sort_order ?? -1;
        const recommended = paid.find(p => p.sort_order > currentSort) || paid[0];
        if (recommended) setSelectedPlan(recommended.id);
      }
      setLoadingPlans(false);
    }).catch(() => setLoadingPlans(false));
  }, [currentPlan]);

  const handleUpgrade = async () => {
    if (loading || success || !selectedPlan) return;
    setLoading(true);
    setError(null);

    try {
      if (!window.Razorpay) {
        setError(t.billingScriptError || "Payment service unavailable. Check your internet connection.");
        setLoading(false);
        return;
      }

      const orderData = await api.createBillingOrder(selectedPlan);

      if (!orderData || !orderData.success) {
        if (orderData && orderData.error === "already_pro") {
          setSuccess(true);
          if (onUpgradeSuccess) await onUpgradeSuccess();
          setTimeout(() => onClose(), 1200);
          return;
        }
        setError(orderData?.error || t.billingOrderError || "Could not create order. Please try again.");
        setLoading(false);
        return;
      }

      const rzp = new window.Razorpay({
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: orderData.name || t.app || "Rajieev House Rental",
        description: orderData.description || "Plan Upgrade",
        order_id: orderData.order_id,
        handler: async function (response) {
          try {
            const verifyResult = await api.verifyBillingPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            if (verifyResult && verifyResult.success) {
              setSuccess(true);
              setLoading(false);
              if (onUpgradeSuccess) await onUpgradeSuccess();
              setTimeout(() => onClose(), 1500);
            } else {
              setError(t.billingVerifyError || "Payment verification failed. Contact support if charged.");
              setLoading(false);
            }
          } catch (verifyErr) {
            console.error("Verify error:", verifyErr);
            setError(t.billingVerifyError || "Payment verification failed. Contact support if charged.");
            setLoading(false);
          }
        },
        modal: { ondismiss: () => setLoading(false) },
        theme: { color: h.ac || "#3b82f6" }
      });
      rzp.open();
    } catch (e) {
      console.error("Upgrade error:", e);
      setError(e.message || "Something went wrong");
      setLoading(false);
    }
  };

  const usagePct = limit > 0 && limit < 999999 ? Math.min((used / limit) * 100, 100) : 0;
  const sel = plans.find(p => p.id === selectedPlan);

  const FEATURE_LABELS = {
    basic_dashboard: "Dashboard", payments: "Rent Payments", maintenance: "Maintenance",
    whatsapp: "WhatsApp Notifications", leases: "Lease Management", service_persons: "Service Persons",
    agreements: "Rental Agreements", commercial: "Commercial Properties", reports: "Reports",
    csv_export: "CSV Export", analytics: "Analytics", automation: "Automation",
    multi_user: "Multi-User Access", priority_support: "Priority Support"
  };

  return <div className="animate-fadeIn" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: h.ov, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: sp.lg }} onClick={onClose}>
    <div className="animate-scaleIn" style={{ ...crdEl(h), padding: sp.xl, maxWidth: 380, width: "100%", boxShadow: h.s3, maxHeight: "90vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>

      {/* Success state */}
      {success ? <div style={{ textAlign: "center", padding: `${sp.xl}px 0` }}>
        <div style={{ width: 56, height: 56, borderRadius: 28, background: `${h.gn}15`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto", marginBottom: sp.md }}>
          <span style={{ fontSize: 28, color: h.gn }}>{"\u2713"}</span>
        </div>
        <h3 style={{ color: h.gn, fontSize: 18, fontWeight: 700, margin: 0 }}>{t.billingSuccess || "Plan Upgraded!"}</h3>
        <p style={{ color: h.ts, fontSize: 12, margin: `${sp.sm}px 0 0` }}>{sel?.name || "Pro"} Plan Active</p>
      </div> : <>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: sp.lg }}>
          <div style={{ width: 48, height: 48, borderRadius: rad.md, background: `${h.ac}12`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto", marginBottom: sp.sm }}>
            <span style={{ fontSize: 24 }}>{"\uD83D\uDE80"}</span>
          </div>
          <h3 style={{ color: h.tx, fontSize: 17, fontWeight: 700, margin: 0 }}>{t.planUpgradeTitle || "Choose Your Plan"}</h3>
          <p style={{ color: h.ts, fontSize: 12, margin: `${sp.xs}px 0 0` }}>
            {t.planChooseDesc || "Upgrade, renew, or change your plan"}
          </p>
        </div>

        {/* Usage indicator */}
        {usagePct > 0 && <div style={{ background: h.sf, borderRadius: rad.sm, padding: sp.md, marginBottom: sp.md }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: sp.xs }}>
            <span style={{ color: h.ts, fontSize: 11 }}>{t.planPropsUsed || "Properties used"}</span>
            <span style={{ color: h.tx, fontWeight: 700, fontSize: 13 }}>{used}<span style={{ color: h.ts, fontWeight: 400 }}> / {limit}</span></span>
          </div>
          <div style={{ height: 5, borderRadius: 3, background: h.ib, overflow: "hidden" }}>
            <div style={{ width: `${usagePct}%`, height: "100%", borderRadius: 3, background: usagePct >= 90 ? h.rd : usagePct >= 70 ? h.wn : h.ac, transition: "width 0.5s ease" }} />
          </div>
        </div>}

        {/* Plans loading */}
        {loadingPlans ? <div style={{ textAlign: "center", padding: sp.xl }}>
          <span style={{ color: h.ts, fontSize: 12 }}>Loading plans...</span>
        </div> : <>

          {/* Plan cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: sp.sm + 4, marginBottom: sp.md }}>
            {(() => {
              // Determine the one recommended plan: next tier above current plan
              const currentSort = plans.find(p => p.id === currentPlan)?.sort_order ?? -1;
              const recommendedId = (plans.find(p => p.sort_order > currentSort) || plans[0])?.id;
              return plans.map(p => {
                const isSelected = selectedPlan === p.id;
                const isRecommended = p.id === recommendedId;
                const isCurrent = p.id === currentPlan;
                return <div key={p.id} onClick={() => setSelectedPlan(p.id)} style={{
                  padding: sp.md, borderRadius: rad.md,
                  border: `2px solid ${isSelected ? (isCurrent ? h.gn : h.ac) : h.bd}`,
                  background: isSelected ? (isCurrent ? `${h.gn}08` : `${h.ac}08`) : "transparent",
                  cursor: "pointer", position: "relative", transition: "all 0.2s",
                  opacity: 1, overflow: "visible"
                }}>
                  {isRecommended && !isCurrent && <span style={{
                    display: "inline-flex", alignItems: "center",
                    background: h.gn, color: "#fff",
                    padding: "3px 12px", borderRadius: 999, fontSize: 9, fontWeight: 700,
                    letterSpacing: 0.3, position: "absolute", top: -11, right: sp.md,
                    zIndex: 2, boxShadow: `0 2px 6px ${h.gn}30`
                  }}>RECOMMENDED</span>}
                  {isCurrent && <span style={{
                    display: "inline-flex", alignItems: "center",
                    background: isSelected ? h.gn : (h.cd || "#fff"), color: isSelected ? "#fff" : h.sc,
                    padding: "3px 12px", borderRadius: 999, fontSize: 9, fontWeight: 700,
                    position: "absolute", top: -11, right: sp.md,
                    zIndex: 2, border: isSelected ? "none" : `1.5px solid ${h.bd}`,
                    boxShadow: isSelected ? `0 2px 6px ${h.gn}30` : "none"
                  }}>{isSelected ? "RENEW" : "CURRENT"}</span>}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ color: isCurrent ? h.sc : h.tx, fontWeight: 700, fontSize: 14 }}>{p.name}</div>
                      <div style={{ color: h.ts, fontSize: 11, marginTop: 2 }}>
                        {p.max_properties >= 999999 ? "Unlimited" : `Up to ${p.max_properties}`} properties
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ color: isCurrent ? h.sc : isSelected ? h.ac : h.tx, fontWeight: 800, fontSize: 18 }}>{formatPrice(p.price)}</div>
                      <div style={{ color: h.tt, fontSize: 10 }}>/month (30 days)</div>
                    </div>
                  </div>
                </div>;
              });
            })()}
          </div>

          {/* Selected plan features */}
          {sel && <div style={{ marginBottom: sp.md }}>
            <div style={{ color: h.tx, fontSize: 11, fontWeight: 600, marginBottom: sp.sm }}>{sel.name} includes:</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: `${sp.xs}px ${sp.sm}px` }}>
              {(sel.features || []).map((f, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: sp.xs }}>
                <span style={{ color: h.gn, fontSize: 10, fontWeight: 700 }}>{"\u2713"}</span>
                <span style={{ color: h.tx, fontSize: 11 }}>{FEATURE_LABELS[f] || f.replace(/_/g, " ")}</span>
              </div>)}
            </div>
          </div>}
        </>}

        {/* Error */}
        {error && <div style={{ background: `${h.rd}10`, border: `1px solid ${h.rd}20`, borderRadius: rad.sm, padding: sp.sm, marginBottom: sp.sm }}>
          <p style={{ color: h.rd, fontSize: 11, margin: 0 }}>{error}</p>
        </div>}

        {/* CTA */}
        <button onClick={handleUpgrade} disabled={loading || !selectedPlan}
          style={{
            ...btnP(h), width: "100%", padding: sp.md, fontSize: 14, fontWeight: 700,
            background: loading ? h.sc : h.ac, marginBottom: sp.xs,
            opacity: loading ? 0.7 : 1, cursor: loading ? "wait" : "pointer",
            boxShadow: loading ? "none" : `0 4px 16px ${h.ac}30`
          }}>
          {loading ? (t.billingProcessing || "Processing...")
            : sel ? (sel.id === currentPlan ? `Renew ${sel.name} \u2014 ${formatPrice(sel.price)}/mo` : `Upgrade to ${sel.name} \u2014 ${formatPrice(sel.price)}/mo`) : "Select a Plan"}
        </button>

        <div style={{ textAlign: "center", marginBottom: sp.xs }}>
          <span style={{ color: h.tt, fontSize: 10 }}>{"\uD83D\uDD12"} {t.billingSecure || "Secured by Razorpay"}</span>
        </div>

        <button onClick={onClose} style={{ background: "none", border: "none", color: h.ts, width: "100%", padding: sp.sm, cursor: "pointer", fontSize: 12 }}>
          {t.planMaybeLater || "Maybe Later"}
        </button>
      </>}
    </div>
  </div>;
}
